/**
 * 文本框拖拽绘制 Hook
 *
 * 当 `active` 为 true 时（即元素面板处于"文本"标签页），在画布空白处按住鼠标拖拽
 * 可绘制一个文本框。松开鼠标后：
 *   - 若拖拽面积足够大，以精确坐标/尺寸创建正文文本元素并立即进入编辑；
 *   - 若拖拽距离过短（视为误触），不创建元素。
 *
 * 有激活页时坐标为页内局部；无激活页时为世界坐标（stageHost + screenToWorld）。
 */

import { useState, useRef, useCallback, useEffect, RefObject } from 'react';
import { screenToWorld } from '@/components/canvas/document/coordinates';
import type { TextInsertVia } from '@/components/canvas/workspace/model/elementInsertTarget';
import type { TextBlockKind } from '@/components/canvas/elements/text/utils/textContent';

export interface TextDrawRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface UseTextDrawOptions {
  /** 是否启用文本绘制模式 */
  active: boolean;
  /** 激活时使用的文本块类型；null/undefined 时退化为默认行为（拖拽→paragraph，单击→h1） */
  blockKind?: TextBlockKind | null;
  /** 当前激活页的 `data-page` DOM */
  pageRef: RefObject<HTMLDivElement | null>;
  /** 无激活页时用于世界坐标换算 */
  stageHostRef?: RefObject<HTMLDivElement | null>;
  panX?: number;
  panY?: number;
  zoom: number;
  isSpacePressed: boolean;
  /** 创建文本元素并返回 id */
  addTextElementAt: (payload: {
    x: number;
    y: number;
    width: number;
    height: number;
    blockKind?: TextBlockKind;
    via?: TextInsertVia;
    pageId?: string;
  }) => string | null;
  /** 单击画布：在坐标处创建默认段落文本框 */
  addParagraphTextAt: (
    x: number,
    y: number,
    opts?: { blockKind?: TextBlockKind; via?: TextInsertVia; pageId?: string },
  ) => string | null;
  /** 进入文本编辑状态 */
  beginTextEdit: (id: string, opts?: { clickX?: number; clickY?: number }) => void;
  /** 绘制完成后回调（用于重置 toggle 状态） */
  onDrawComplete?: () => void;
}

/** 拖拽绘制的最小有效尺寸（画布像素），小于此则视为误触 */
const MIN_DRAW_SIZE = 20;

export function useTextDraw({
  active,
  blockKind,
  pageRef,
  stageHostRef,
  panX = 0,
  panY = 0,
  zoom,
  isSpacePressed,
  addTextElementAt,
  addParagraphTextAt,
  beginTextEdit,
  onDrawComplete,
}: UseTextDrawOptions) {
  const [drawRect, setDrawRect] = useState<TextDrawRect | null>(null);

  const startRef = useRef<{ x: number; y: number } | null>(null);
  const insertTargetRef = useRef<{ via: TextInsertVia; pageId?: string }>({
    via: 'workspace',
  });
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const panXRef = useRef(panX);
  panXRef.current = panX;
  const panYRef = useRef(panY);
  panYRef.current = panY;
  const blockKindRef = useRef(blockKind);
  blockKindRef.current = blockKind;
  const addTextElementAtRef = useRef(addTextElementAt);
  addTextElementAtRef.current = addTextElementAt;
  const addParagraphTextAtRef = useRef(addParagraphTextAt);
  addParagraphTextAtRef.current = addParagraphTextAt;
  const beginTextEditRef = useRef(beginTextEdit);
  beginTextEditRef.current = beginTextEdit;
  const onDrawCompleteRef = useRef(onDrawComplete);
  onDrawCompleteRef.current = onDrawComplete;

  const toDrawCoords = useCallback((clientX: number, clientY: number) => {
    const pageEl = pageRef.current;
    if (pageEl) {
      const rect = pageEl.getBoundingClientRect();
      return {
        x: (clientX - rect.left) / zoomRef.current,
        y: (clientY - rect.top) / zoomRef.current,
      };
    }
    const host = stageHostRef?.current;
    if (host) {
      const rect = host.getBoundingClientRect();
      return screenToWorld(
        clientX,
        clientY,
        rect,
        panXRef.current,
        panYRef.current,
        zoomRef.current,
      );
    }
    return { x: 0, y: 0 };
  }, [pageRef, stageHostRef]);

  useEffect(() => {
    if (!active) {
      startRef.current = null;
      setDrawRect(null);
      return;
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!startRef.current) return;
      const pos = toDrawCoords(e.clientX, e.clientY);
      const { x: sx, y: sy } = startRef.current;
      setDrawRect({
        x: Math.min(sx, pos.x),
        y: Math.min(sy, pos.y),
        width: Math.abs(pos.x - sx),
        height: Math.abs(pos.y - sy),
      });
    };

    const onMouseUp = (e: MouseEvent) => {
      if (!startRef.current) return;
      const pos = toDrawCoords(e.clientX, e.clientY);
      const { x: sx, y: sy } = startRef.current;
      startRef.current = null;
      setDrawRect(null);

      const x = Math.min(sx, pos.x);
      const y = Math.min(sy, pos.y);
      const width = Math.abs(pos.x - sx);
      const height = Math.abs(pos.y - sy);

      const finishWithText = (id: string | null, clickX?: number, clickY?: number) => {
        if (!id) {
          onDrawCompleteRef.current?.();
          return;
        }
        const blockClick = (ev: MouseEvent) => {
          ev.stopPropagation();
          window.removeEventListener('click', blockClick, true);
        };
        window.addEventListener('click', blockClick, true);
        requestAnimationFrame(() => {
          beginTextEditRef.current(
            id,
            clickX != null && clickY != null ? { clickX, clickY } : undefined,
          );
          onDrawCompleteRef.current?.();
        });
      };

      const insertOpts = insertTargetRef.current;

      const kind = blockKindRef.current ?? undefined;

      if (width < MIN_DRAW_SIZE || height < MIN_DRAW_SIZE) {
        finishWithText(
          addParagraphTextAtRef.current(sx, sy, { ...insertOpts, blockKind: kind }),
          sx,
          sy,
        );
        return;
      }

      finishWithText(
        addTextElementAtRef.current({ x, y, width, height, blockKind: kind, ...insertOpts }),
      );
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [active, toDrawCoords]);

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!active) return false;
      if (e.button !== 0) return false;
      if (isSpacePressed) return false;

      const target = e.target as HTMLElement;
      if (target.closest('[data-element-hit]') || target.closest('[data-handle]')) return false;

      e.preventDefault();
      if (pageRef.current) {
        insertTargetRef.current = {
          via: 'page',
          pageId: pageRef.current.getAttribute('data-page') ?? undefined,
        };
      } else {
        insertTargetRef.current = { via: 'workspace' };
      }
      startRef.current = toDrawCoords(e.clientX, e.clientY);
      setDrawRect({ x: startRef.current.x, y: startRef.current.y, width: 0, height: 0 });
      return true;
    },
    [active, isSpacePressed, toDrawCoords],
  );

  return {
    drawRect,
    handleCanvasMouseDown,
    isDrawing: drawRect !== null,
  };
}
